import { useCallback, useEffect, useState } from 'react';
import { useLoadingState } from '../hooks';
import { TwindenLoader } from '../components/brand/TwindenLoader';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import {
  Alert,
  AlertIcon,
  Button,
  Center,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { KeyRound, Send } from 'lucide-react';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useAuth } from '../hooks/useAuth';
import { api, ApiError, type JoinLinkInfo } from '../lib/api';
import { currentFamilyIdAtom } from '../store';
import { WelcomeShell } from '../components/page/WelcomeShell';
import { JoinInTheApp } from '../components/site/StoreBadges';
import {
  HandlePicker,
  type HandleValue,
} from '../components/family/HandlePicker';
import { ContinueAs, JoinChoice } from '../components/family/JoinChoice';
import { guessDeviceName } from '../lib/devices';
import { joinErrorMessage } from './JoinFamilyPage';
import { signInErrorMessage } from './LoginPage';
import { t as translateNow, useT } from '../i18n';

// The family link's join page, /join/f/:secret (D153). It only ever makes a
// request: a root or admin of the family lets the person in (D154, D156).

type Step = 'choose' | 'confirm' | 'new' | 'ask' | 'sent';

type PageState =
  | { status: 'loading' }
  | { status: 'unsupported' }
  | { status: 'dead'; message: string }
  | { status: 'ready'; info: JoinLinkInfo };

const NAME_PAUSE_MS = 500;
const NOTE_MAX = 200;

const isDeadLink = (err: unknown) =>
  err instanceof ApiError &&
  (err.code === 'LINK_RESET' || err.code === 'LINK_OFF');

export function JoinLinkPage() {
  const { secret = '' } = useParams<{ secret: string }>();
  const [searchParams] = useSearchParams();
  const via = searchParams.get('via') ?? undefined;
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const setCurrentFamilyId = useSetAtom(currentFamilyIdAtom);
  const {
    currentUser,
    isAuthenticated,
    logout,
    signInWithPasskey,
    joinWithFamilyLink,
  } = useAuth();

  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [chosenStep, setStep] = useState<Step>(
    isAuthenticated ? 'confirm' : 'choose'
  );
  // Signed out meanwhile (another tab, an expired session): ask again.
  const step: Step =
    !currentUser && (chosenStep === 'confirm' || chosenStep === 'ask')
      ? 'choose'
      : chosenStep;
  const [busy, setBusy] = useState(false);
  // The mark in the shell while the server answers (M66, D248).
  const waiting = useLoadingState({ loading: state.status === 'loading' });
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [pausedName, setPausedName] = useState('');
  const [handle, setHandle] = useState<HandleValue | null>(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!browserSupportsWebAuthn()) {
      setState({ status: 'unsupported' });
      return;
    }
    api
      .getJoinLinkInfo(secret, via)
      .then((res) => setState({ status: 'ready', info: res.data }))
      .catch((err) =>
        setState({
          status: 'dead',
          message: isDeadLink(err)
            ? translateNow('account.join.deadLink')
            : joinErrorMessage(err),
        })
      );
  }, [secret, via]);

  // Suggestions follow the name once typing pauses (D147).
  useEffect(() => {
    const timer = setTimeout(() => {
      setPausedName(name.trim());
      setHandle(null);
    }, NAME_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [name]);

  const suggest = useCallback(
    async ({ gendered }: { gendered: boolean }) =>
      (await api.getJoinLinkSuggestions(secret, pausedName, gendered)).data
        .suggestions,
    [secret, pausedName]
  );

  const familyName = state.status === 'ready' ? state.info.familyName : '';

  // Shared by both ways of asking: what each refusal means here (D155).
  const handleRefusal = (err: unknown): boolean => {
    if (!(err instanceof ApiError)) return false;
    if (isDeadLink(err)) {
      setState({
        status: 'dead',
        message: translateNow('account.join.deadLink'),
      });
      return true;
    }
    switch (err.code) {
      case 'ALREADY_MEMBER':
        if (state.status === 'ready') setCurrentFamilyId(state.info.familyId);
        toast({
          status: 'info',
          title: translateNow('account.join.alreadyIn', {
            family: familyName,
          }),
        });
        navigate('/', { replace: true });
        return true;
      case 'REQUEST_PENDING':
        setStep('sent');
        return true;
      case 'TRY_LATER':
        setError(
          translateNow('account.join.notAddedThisTime', { family: familyName })
        );
        return true;
      case 'TOO_MANY_REQUESTS':
        setError(translateNow('account.join.tooMany', { family: familyName }));
        return true;
      default:
        return false;
    }
  };

  const optional = () => ({
    ...(note.trim() ? { note: note.trim() } : {}),
    ...(via ? { via } : {}),
  });

  const signIn = async () => {
    setError(null);
    setBusy(true);
    try {
      await signInWithPasskey();
      setStep('ask');
    } catch (err) {
      setError(signInErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    await logout().catch(() => undefined);
    setError(null);
    setStep('choose');
  };

  const ask = async () => {
    setError(null);
    setBusy(true);
    try {
      await api.askToJoin(secret, optional());
      setStep('sent');
    } catch (err) {
      if (!handleRefusal(err))
        setError(
          err instanceof ApiError
            ? err.message
            : translateNow('common.somethingWrong')
        );
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    if (!handle?.token || !name.trim()) return;
    setError(null);
    setBusy(true);
    try {
      await joinWithFamilyLink(secret, {
        displayName: name.trim(),
        handle: handle.handle,
        handleToken: handle.token,
        deviceName: guessDeviceName() || undefined,
        ...optional(),
      });
      setStep('sent');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'HANDLE_TAKEN') {
        // Someone took it a moment ago: here's another (D147).
        setError(
          translateNow('account.handle.takenReplaced', {
            handle: handle.handle,
          })
        );
        setHandle(err.suggestion ?? null);
      } else if (
        err instanceof ApiError &&
        err.code === 'HANDLE_NOT_SUGGESTED'
      ) {
        // The suggestion lapsed (30 minutes, #69): fetch a new one.
        setHandle(null);
        setError(translateNow('account.login.tryAgain'));
      } else if (!handleRefusal(err)) {
        setError(joinErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  if (state.status === 'loading') {
    return (
      <WelcomeShell title={t('account.join.joining')}>
        <Center>{waiting.loader && <TwindenLoader />}</Center>
      </WelcomeShell>
    );
  }

  if (state.status === 'unsupported') {
    return (
      <WelcomeShell
        title={t('account.join.unsupportedTitle')}
        subtitle={t('account.join.unsupportedBody')}
      >
        {null}
      </WelcomeShell>
    );
  }

  if (state.status === 'dead') {
    return (
      <WelcomeShell title={t('account.join.errorTitle')}>
        <Alert status="warning" borderRadius="xl">
          <AlertIcon />
          {state.message}
        </Alert>
      </WelcomeShell>
    );
  }

  const { info } = state;
  const title = t('account.join.linkTitle', { family: info.familyName });
  const sharedBy = info.sharedBy
    ? t('account.join.sharedBy', { name: info.sharedBy })
    : undefined;

  const errorAlert = error && (
    <Alert status="error" borderRadius="xl">
      <AlertIcon />
      {error}
    </Alert>
  );

  const noteField = (
    <FormControl>
      <FormLabel>{t('account.join.note')}</FormLabel>
      <Textarea
        name="note"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder={t('account.join.notePlaceholder')}
        maxLength={NOTE_MAX}
        rows={2}
        resize="none"
      />
    </FormControl>
  );

  if (step === 'sent') {
    return (
      <WelcomeShell
        title={t('account.join.waitingTitle', { family: info.familyName })}
        handle={info.familyHandle}
        subtitle={t('account.join.waitingBody')}
      >
        <Button
          h="56px"
          fontSize="17px"
          w="full"
          onClick={() => navigate('/', { replace: true })}
        >
          {t('account.join.openTwinden')}
        </Button>
      </WelcomeShell>
    );
  }

  if (step === 'choose') {
    return (
      <WelcomeShell
        title={title}
        handle={info.familyHandle}
        subtitle={sharedBy}
        footer={t('account.join.chooseFooter')}
      >
        <JoinChoice
          onNew={() => {
            setError(null);
            setStep('new');
          }}
          onSignIn={signIn}
          signingIn={busy}
          error={error}
        />
        {/* On a phone, the app instead (D346): this link opens in it. */}
        <JoinInTheApp />
      </WelcomeShell>
    );
  }

  if (step === 'confirm' && currentUser) {
    return (
      <WelcomeShell
        title={title}
        handle={info.familyHandle}
        subtitle={sharedBy}
      >
        <ContinueAs
          name={currentUser.displayName}
          onContinue={() => setStep('ask')}
          onSignOut={signOut}
        />
      </WelcomeShell>
    );
  }

  if (step === 'new') {
    return (
      <WelcomeShell
        title={title}
        handle={info.familyHandle}
        subtitle={sharedBy}
        footer={t('account.join.newFooter')}
      >
        <VStack spacing={4} align="stretch">
          {errorAlert}
          <FormControl isRequired>
            <FormLabel>{t('account.join.yourName')}</FormLabel>
            <Input
              h="52px"
              name="displayName"
              autoComplete="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('account.join.yourNamePlaceholder')}
              maxLength={100}
            />
          </FormControl>
          {pausedName ? (
            <HandlePicker
              label={t('account.join.youllBe')}
              suggest={suggest}
              value={handle}
              onChange={setHandle}
            />
          ) : null}
          {noteField}
          <Button
            h="56px"
            fontSize="17px"
            leftIcon={<KeyRound size={20} />}
            onClick={create}
            isLoading={busy}
            isDisabled={!name.trim() || !handle?.token}
          >
            {t('account.join.createPasskey')}
          </Button>
        </VStack>
      </WelcomeShell>
    );
  }

  // ask: someone already on Twinden (signed in now)
  return (
    <WelcomeShell title={title} handle={info.familyHandle} subtitle={sharedBy}>
      <VStack spacing={4} align="stretch">
        {errorAlert}
        {noteField}
        <Button
          h="56px"
          fontSize="17px"
          leftIcon={<Send size={20} />}
          onClick={ask}
          isLoading={busy}
        >
          {t('account.join.askToJoin')}
        </Button>
      </VStack>
    </WelcomeShell>
  );
}
